import React, { useState } from 'react'
import { Button, Col, message, Row, Spin } from 'antd'
import getBroadcastReportExport, {
	BroadcastReportTypes,
} from 'api/broadcasts/getBroadcastReportExport'
import handleAxiosError from 'utils/handleAxiosError'
import downloadUsingAPI from 'utils/downloadUsingAPI'
import toTileCase from 'utils/toTitleCase'
import { RiDownload2Fill } from 'react-icons/ri'
import toPrecisionAmount from 'utils/toPrecisionAmount'
import getCurrencySymbol from 'utils/getCurrencySymbol'
import { useAppSelector } from 'hooks/reduxHooks'
import styles from './styles.module.scss'

const columnWidth = 8

interface PropTypes {}

const BroadcastReport: React.FC<PropTypes> = () => {
	const currencySelected = useAppSelector(
		state => state.broadcast.broadcast?.client.currency_selected
	)

	const {
		broadcast,
		insights,
		loading: loadingBroadcast,
	} = useAppSelector(state => state.broadcast)

	const broadcastCostCalculated =
		broadcast?.status === 'COMPLETED' && broadcast?.cost_consumed > -1

	const [generatingReport, setGeneratingReport] = useState({
		ALL: false,
		SENT: false,
		READ: false,
		DELIVERED: false,
		FAILED: false,
		REPLY: false,
	})

	const validateBroadcast = () => {
		if (broadcast === null) {
			throw new Error('Broadcast is null')
		}
	}

	const downloadReport = async (type: BroadcastReportTypes) => {
		try {
			setGeneratingReport(state => ({ ...state, [type]: true }))

			validateBroadcast()

			const options = {
				id: broadcast!._id,
				type,
			}

			await downloadUsingAPI(
				await getBroadcastReportExport(options),
				`${broadcast!.name}-${type}-REPORT.csv`
			)

			message.success(
				`Broadcast: ${broadcast!.name}, ${toTileCase(
					type
				)} report has been downloaded.`
			)

			setGeneratingReport(state => ({ ...state, [type]: false }))
		} catch (error: any) {
			setGeneratingReport(state => ({ ...state, [type]: false }))
			handleAxiosError(error)
		}
	}

	return (
		<Spin spinning={loadingBroadcast} tip='Loading Reports'>
			<div className={styles.broadcastReport}>
				<Row>
					<Col span={columnWidth}>Stats</Col>
					<Col span={columnWidth}>Value</Col>
					<Col span={columnWidth}>Action</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						Total Contacts
					</Col>
					<Col span={columnWidth}>{broadcast?.contactSelected || 0}</Col>
					<Col span={columnWidth}>-</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						Replies
					</Col>
					<Col span={columnWidth}>{insights?.replies}</Col>
					<Col span={columnWidth}>
						<Button
							type='link'
							onClick={() => downloadReport('REPLY')}
							loading={generatingReport.REPLY}
						>
							Download Report
						</Button>
					</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						Sent
					</Col>
					<Col span={columnWidth}>{broadcast?.totalSent || 0}</Col>
					<Col span={columnWidth}>
						<Button
							type='link'
							onClick={() => downloadReport('SENT')}
							loading={generatingReport.SENT}
						>
							Download Report
						</Button>
					</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						Delivered
					</Col>
					<Col span={columnWidth}>{insights?.deliveredCount || 0}</Col>
					<Col span={columnWidth}>
						<Button
							type='link'
							onClick={() => downloadReport('DELIVERED')}
							loading={generatingReport.DELIVERED}
						>
							Download Report
						</Button>
					</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						Undelivered
					</Col>
					<Col span={columnWidth}>{insights?.failedCount || 0}</Col>
					<Col span={columnWidth}>
						<Button
							type='link'
							onClick={() => downloadReport('FAILED')}
							loading={generatingReport.FAILED}
						>
							Download Report
						</Button>
					</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						Read
					</Col>
					<Col span={columnWidth}>{insights?.readCount || 0}</Col>
					<Col span={columnWidth}>
						<Button
							type='link'
							onClick={() => downloadReport('READ')}
							loading={generatingReport.READ}
						>
							Download Report
						</Button>
					</Col>
					<Col span={columnWidth} className={styles.broadcastReport_label}>
						{broadcastCostCalculated ? 'Final' : 'Estimated'} Cost
					</Col>
					<Col span={columnWidth}>
						{getCurrencySymbol(broadcast?.currency || currencySelected || 'INR')}{' '}
						{toPrecisionAmount(
							broadcastCostCalculated ? broadcast.cost_consumed : broadcast?.cost || 0
						)}
					</Col>
					<Col span={columnWidth}>-</Col>
				</Row>
				<Button
					loading={generatingReport.ALL}
					className={styles.broadcastReport_button}
					onClick={() => downloadReport('ALL')}
					icon={<RiDownload2Fill />}
				>
					Delivery Report
				</Button>
			</div>
		</Spin>
	)
}

export default BroadcastReport
